/**
 * Service for managing Microsoft Graph email monitoring (webhook) subscriptions
 * via the public Outlook API Gateway (JWT-authenticated endpoints).
 */
import config from '../config';

const BASE_URL = config.api.outlookServiceUrl;

// Explicit per-environment outlook bases so admin tooling can target QA or prod
// regardless of which build is running.
const ENV_BASE_URLS = {
  qa: config.api.outlookServiceUrlQA,
  prod: config.api.outlookServiceUrlProd,
};

class OutlookService {
  /**
   * List active email monitoring subscriptions.
   * Admins see all; regular users see only their own.
   *
   * @param {string} token - Bearer JWT token.
   * @returns {Promise<{subscriptions: Array, total_count: number}>}
   */
  async getSubscriptions(token) {
    const response = await fetch(`${BASE_URL}/outlook/subscriptions`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to list subscriptions (${response.status})`);
    }

    return response.json();
  }

  /**
   * Subscribe a user to email monitoring.
   * Admins may subscribe any email; regular users may only subscribe their own.
   *
   * @param {string} token - Bearer JWT token.
   * @param {string} userEmail - Mailbox UPN to subscribe.
   * @returns {Promise<Object>} - New subscription object.
   */
  async subscribeUser(token, userEmail) {
    const response = await fetch(`${BASE_URL}/outlook/subscriptions`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_email: userEmail }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to subscribe (${response.status})`);
    }

    return response.json();
  }

  /**
   * Unsubscribe a user from email monitoring.
   * Admins may unsubscribe any email; regular users may only remove their own.
   *
   * @param {string} token - Bearer JWT token.
   * @param {string} userEmail - Mailbox UPN to unsubscribe.
   * @returns {Promise<Object>}
   */
  async unsubscribeUser(token, userEmail) {
    const encodedEmail = encodeURIComponent(userEmail);
    const response = await fetch(
      `${BASE_URL}/outlook/subscriptions/${encodedEmail}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to unsubscribe (${response.status})`);
    }

    return response.json();
  }

  /**
   * Create (or refresh) the Graph change-notification subscription on the
   * shared client-news trigger mailbox (clientnews@hffinancial.com).  New mail
   * there is summarised and posted to the Client News Teams channel.
   * Idempotent — returns the existing subscription if one is already active.
   *
   * @param {string} token - Bearer JWT token.
   * @param {('qa'|'prod')} [env] - Target environment.  Omit to use the build's
   *   default outlook URL; pass 'qa'/'prod' to explicitly target that gateway.
   * @returns {Promise<Object>} - The active subscription object
   *   (subscription_id, notification_url, expiration_datetime, …).
   */
  async subscribeClientNews(token, env) {
    const base = (env && ENV_BASE_URLS[env]) || BASE_URL;
    const response = await fetch(
      `${base}/outlook/webhook/subscribe-client-news`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to subscribe client news (${response.status})`);
    }

    return response.json();
  }

  /**
   * Delete a Graph change-notification subscription by id.  The subscription
   * pool is global to the Azure app, so any reachable gateway can delete any
   * subscription; defaults to the build's own outlook URL (CORS-safe).
   *
   * @param {string} token - Bearer JWT token.
   * @param {string} subscriptionId - Graph subscription id to delete.
   * @param {('qa'|'prod')} [env] - Target gateway; omit for the build default.
   * @returns {Promise<Object>}
   */
  async deleteWebhookSubscription(token, subscriptionId, env) {
    const base = (env && ENV_BASE_URLS[env]) || BASE_URL;
    const response = await fetch(
      `${base}/outlook/webhook/subscribe/${encodeURIComponent(subscriptionId)}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to delete subscription (${response.status})`);
    }

    return response.json().catch(() => ({}));
  }

  /**
   * List all active Graph change-notification subscriptions (the shared pool).
   * Returns raw Graph objects (camelCase: notificationUrl, resource,
   * expirationDateTime).  The pool is global to the Azure app, so any gateway
   * returns the same list.
   *
   * @param {string} token - Bearer JWT token.
   * @returns {Promise<{subscriptions: Array, total_count: number}>}
   */
  async getWebhookSubscriptions(token) {
    const response = await fetch(`${BASE_URL}/outlook/webhook/subscriptions`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to list webhook subscriptions (${response.status})`);
    }

    return response.json();
  }

  /**
   * Map a subscription's notificationUrl back to an environment label by
   * matching it against the configured QA / prod outlook bases.
   *
   * @param {string} url - The subscription's notificationUrl.
   * @returns {('qa'|'prod'|null)}
   */
  envForNotificationUrl(url) {
    if (!url) return null;
    if (ENV_BASE_URLS.qa && url.startsWith(ENV_BASE_URLS.qa)) return 'qa';
    if (ENV_BASE_URLS.prod && url.startsWith(ENV_BASE_URLS.prod)) return 'prod';
    return null;
  }
}

const outlookService = new OutlookService();
export default outlookService;
